/**
 * `/app/billing` — the agency's own subscription to Super Agency.
 *
 * Task 301 (Lane B), extended by Task 301B. Specification:
 * `tasks/295A-commercial-foundation-and-staging.md` §6.4, §8.3. Task
 * files: `tasks/301-saas-subscription-catalogue-and-billing.md` §7 and
 * `tasks/301B-agency-subscription-usage-and-catalogue.md` §6.
 *
 * ---------------------------------------------------------------------
 * WHAT THIS PAGE IS, AND WHAT IT IS NOT. This is the agency's SaaS
 * subscription to Super Agency. It is NOT the agency's service packages
 * sold to its own clients — those live at `/app/services` — and it is not
 * the agency-client contract, and not paid media. The copy says so, in
 * Arabic, at the top of the page, because 295A §11.B1 / CLP-1.7 record
 * that these concepts share vocabulary and are routinely blurred.
 *
 * TASK 300A — THE VOCABULARY INVARIANTS THIS FILE HOLDS.
 *
 *   - «خطة» is the SaaS plan. «باقة» is reserved for the packages an
 *     agency sells its own clients (D4 / CLP-1.7), and appears here
 *     only where the page points AT that layer.
 *   - No state label advertises a trial. `trialing` is an operator
 *     grant, not a product (CLP-1.1).
 *   - The subscription and commercial-standing labels share no word,
 *     and neither borrows the other axis's vocabulary. Both render as
 *     pills on this page.
 *
 * `commercialCopy.shape.test.ts` enforces all three. The words that
 * were tried and rejected, and why, are in
 * `tasks/300A-in-app-commercial-copy-corrections.md` §5.
 *
 * NO PAYMENT SURFACE. Launch is manual activation and bank transfer
 * (295A D1 / CLP-1.1, CLP-1.2). There is no checkout button, no provider
 * SDK, no invoice list and no upgrade action here — and the page says
 * that honestly rather than implying a payment path that does not exist.
 *
 * TASK 301B ADDS FOUR READ-ONLY BLOCKS AND NO ACTION. Usage, effective
 * capabilities, a plan comparison, and one navigation link to the
 * existing `/app/support` page. The link SUBMITS NOTHING and the copy
 * beside it says so: there is no request table, no mutation, and no
 * "your request was received" claim anywhere in this file.
 *
 * NOTHING TECHNICAL REACHES THE READER: plan NAMES, usage NUMBERS and
 * Arabic SENTENCES only — never an identifier, a wire code or a blank
 * live region (Task 301B QA amendment, verification record §9).
 *
 * STRUCTURE IS DELIBERATELY PLAIN. Every block is a sibling `card`
 * section built from the shell's existing class vocabulary, so a later
 * designer overhaul replaces presentation without touching the wire
 * contract or the label maps. Billing remains an extension point here —
 * and no dormant payment code is introduced to occupy it.
 *
 * AUTHORIZATION. `RequirePermission anyOf={[PERM.BILLING_READ]} agencyOnly`
 * gates the route and the same gate hides the nav entry. Both are UX. The
 * load-bearing controls are server-side: `get-agency-commercial-summary`
 * requires an agency-category role, an ACTIVATED commercial standing and
 * `billing.read`, every row it returns is additionally gated by RLS keyed
 * on that same permission, and the usage counts come from a no-argument
 * definer RPC that repeats all of it (`SECURITY.md §9` — a paywalled
 * surface must not depend on hiding a button).
 * ---------------------------------------------------------------------
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isApiError, isNetworkError } from '../api/client';
import { getAgencyCommercialSummary } from '../api/endpoints';
import type {
  AgencyCommercialSummaryData,
  CommercialState,
  PaymentStatusWire,
  PlanCatalogueEntry,
  PlanFeatureAvailability,
  PlanLimitWire,
  ProductFeatureId,
  SubscriptionStateWire,
} from '../api/types';
import { PRODUCT_FEATURE_IDS } from '../api/types';
import { ErrorState, LoadingState } from '../components/states';

export const SUBSCRIPTION_STATE_LABELS_AR: Readonly<Record<SubscriptionStateWire, string>> = {
  trialing: 'سماح إداري',
  active: 'نشط',
  past_due: 'متأخر السداد',
  suspended: 'مجمّد',
  cancelled: 'ملغى',
};

export const COMMERCIAL_STATE_LABELS_AR: Readonly<Record<CommercialState, string>> = {
  pending_activation: 'بانتظار المراجعة',
  activated: 'مفعّل',
  suspended: 'موقوف',
  terminated: 'منتهٍ',
};

export const PAYMENT_STATUS_LABELS_AR: Readonly<Record<PaymentStatusWire, string>> = {
  unpaid: 'غير مسجّل الدفع',
  paid: 'مسجّل الدفع',
};

/**
 * Task 301B — product Arabic for the six capabilities.
 *
 * THE WIRE CARRIES PRODUCT IDS, NOT FEATURE KEYS. `publishing.enabled`
 * and its siblings are entitlement-store vocabulary; they never reach
 * this file and are never rendered. The labels below match the words the
 * rest of the product already uses in the sidebar and the client
 * workspace, so a reader recognises the capability rather than decoding
 * it.
 */
export const PRODUCT_FEATURE_LABELS_AR: Readonly<Record<ProductFeatureId, string>> = {
  automated_publishing: 'النشر الآلي',
  report_export: 'تصدير التقارير',
  creative_intelligence: 'ذكاء الإبداع',
  paid_media: 'الإعلانات المدفوعة',
  paid_media_intelligence: 'ذكاء الإعلانات المدفوعة',
  influencer_marketing: 'التسويق عبر المؤثرين',
};

/**
 * Render one numeric limit.
 *
 * `null` MEANS UNLIMITED and MUST NOT become `0` (295A §6.4, §8.3
 * criterion 3). The production tenant runs on `agency_v1_default`, whose
 * grandfather branch resolves `client.limit` to `null`
 * (`_shared/entitlements.ts:552-555`); rendering that as `0` would tell
 * the live production agency it may create zero clients.
 *
 * `0` is a REAL value with the opposite meaning — zero capacity — so it
 * is rendered as `0` and never as "unlimited".
 *
 * AND THE PAGE MUST NOT EXPLAIN WHY IT IS ZERO. Zero arrives by two
 * indistinguishable routes: an unconfigured `custom_v1` tenant
 * fail-closing (`_shared/entitlements.ts:599-602`), and an operator
 * DELIBERATELY configuring a zero cap — a seat freeze, say. The wire
 * contract carries no provenance field, so any copy that names a cause
 * is a guess presented as a fact. The zero hint states the capacity and
 * stops there.
 *
 * Exported as a pure function so the null-vs-zero rule is unit-tested,
 * not grepped.
 */
export function formatLimitAr(value: number | null): string {
  if (value === null) return 'غير محدود';
  return String(value);
}

/**
 * Task 301B — usage against the cap, as one phrase.
 *
 * Delegates the cap half to {@link formatLimitAr}, so "null means
 * unlimited, zero stays zero" cannot drift between the two cards.
 * `3 من 5`, `12 من غير محدود`, `0 من 0`.
 *
 * The separator is the Arabic word «من» rather than a slash on purpose:
 * a neutral character between two digit runs in an RTL paragraph is
 * reordered by the bidi algorithm, and `3 / 5` can render as `5 / 3`.
 * A strong RTL word between them is unambiguous in every direction.
 */
export function formatUsageAr(used: number, limit: number | null): string {
  return `${used} من ${formatLimitAr(limit)}`;
}

/** Task 301B — the effective per-capability verdict for the current plan. */
export function formatIncludedAr(included: boolean): string {
  return included ? 'مشمولة' : 'غير مشمولة';
}

/**
 * Task 301B — the catalogue's three-valued answer.
 *
 * «حسب الاتفاق» is the honest word for `custom_v1`, which ships with
 * ZERO `plan_features` rows (`20260518120000:120-124`). Rendering it as
 * «غير مشمولة» would claim the plan grants nothing; rendering it as
 * «غير محدود» would claim it grants everything. Neither is true.
 */
export function formatAvailabilityAr(availability: PlanFeatureAvailability): string {
  if (availability === 'included') return 'مشمولة';
  if (availability === 'not_included') return 'غير مشمولة';
  return 'حسب الاتفاق';
}

/** Task 301B — a catalogue cap. Same three-valued rule as the features. */
export function formatPlanLimitAr(limit: PlanLimitWire): string {
  if (limit.kind === 'limited') return String(limit.value);
  if (limit.kind === 'unlimited') return 'غير محدود';
  return 'حسب الاتفاق';
}

/** Only `active` permits client creation (`_shared/entitlements.ts:498`). */
export function subscriptionPermitsClientCreation(state: SubscriptionStateWire): boolean {
  return state === 'active';
}

/** One entry of the effective capability list. */
export type EffectiveFeature = AgencyCommercialSummaryData['features'][number];

/**
 * The usage block, or `null` when the server did not send one.
 *
 * Takes `unknown` deliberately: the payload is a network response cast to
 * a type, and during a rollout window the deployed function is the one
 * that predates Task 301B. A partial or non-numeric block is treated as
 * absent rather than rendered — a usage number that is not a number is
 * not a usage number.
 */
export function asUsage(value: unknown): AgencyCommercialSummaryData['usage'] | null {
  if (!value || typeof value !== 'object') return null;
  const { clients, seats } = value as { clients?: unknown; seats?: unknown };
  if (typeof clients !== 'number' || typeof seats !== 'number') return null;
  return { clients, seats };
}

/** A wire list, or `null` when the server did not send one. Same reason. */
export function asList<T>(value: unknown): ReadonlyArray<T> | null {
  return Array.isArray(value) ? (value as ReadonlyArray<T>) : null;
}

function isProductFeatureId(value: unknown): value is ProductFeatureId {
  return (
    typeof value === 'string' && (PRODUCT_FEATURE_IDS as ReadonlyArray<string>).includes(value)
  );
}

function isAvailability(value: unknown): value is PlanFeatureAvailability {
  return value === 'included' || value === 'not_included' || value === 'by_agreement';
}

/** Exactly one of the wire's three cap shapes, and a `limited` carries a number. */
function isPlanLimitWire(value: unknown): value is PlanLimitWire {
  if (!value || typeof value !== 'object') return false;
  const { kind } = value as { kind?: unknown };
  if (kind === 'unlimited' || kind === 'by_agreement') return true;
  if (kind !== 'limited') return false;
  const { value: cap } = value as { value?: unknown };
  return typeof cap === 'number' && Number.isFinite(cap);
}

/**
 * The effective capability list, or `null` when the server did not send a
 * usable one.
 *
 * `asList` answers "is it an array"; it says nothing about what is inside,
 * and the entries are read straight into JSX. Two different failures get
 * two different answers here, deliberately:
 *
 * - **A capability this client cannot NAME is skipped.** The label map is
 *   closed over `ProductFeatureId`; an id outside it rendered a real
 *   verdict («مشمولة») beside an empty label. Skipping keeps the wire
 *   forward-compatible — a seventh server capability must not blank the
 *   card — while never printing a verdict nobody can attribute.
 * - **A capability this client CAN name but whose verdict is not a
 *   boolean rejects the whole block.** «مشمولة» and «غير مشمولة» are the
 *   only two honest answers; a coerced third is a claim about the
 *   agency's entitlements, and the card is better absent than wrong.
 */
export function asFeatureList(value: unknown): AgencyCommercialSummaryData['features'] | null {
  const raw = asList<unknown>(value);
  if (!raw) return null;
  const out: Array<{ id: ProductFeatureId; included: boolean }> = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') return null;
    const { id, included } = entry as { id?: unknown; included?: unknown };
    if (!isProductFeatureId(id)) continue;
    if (typeof included !== 'boolean') return null;
    out.push({ id, included });
  }
  return out;
}

/**
 * The public catalogue, or `null` when the server did not send a usable one.
 *
 * A malformed ENTRY rejects the WHOLE catalogue rather than being skipped,
 * and that asymmetry with `asFeatureList` is the point: a comparison table
 * silently missing a plan is a wrong comparison published as fact, which
 * is the same failure `get-agency-commercial-summary` refuses server-side
 * (`requireCatalogueEntry` throws on one bad entry rather than dropping
 * it). The client mirrors the server's stance instead of inventing a
 * softer one. Unknown feature ids inside an otherwise valid entry are
 * skipped for the reason above.
 */
export function asCatalogue(value: unknown): ReadonlyArray<PlanCatalogueEntry> | null {
  const raw = asList<unknown>(value);
  if (!raw) return null;
  const out: PlanCatalogueEntry[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') return null;
    const { key, name_en, name_ar, is_current, limits, features } = entry as {
      key?: unknown;
      name_en?: unknown;
      name_ar?: unknown;
      is_current?: unknown;
      limits?: unknown;
      features?: unknown;
    };
    if (typeof key !== 'string' || key.length === 0) return null;
    if (typeof name_en !== 'string' || typeof name_ar !== 'string') return null;
    if (typeof is_current !== 'boolean') return null;
    if (!limits || typeof limits !== 'object') return null;
    const { max_clients, max_seats } = limits as { max_clients?: unknown; max_seats?: unknown };
    if (!isPlanLimitWire(max_clients) || !isPlanLimitWire(max_seats)) return null;
    const rawFeatures = asList<unknown>(features);
    if (!rawFeatures) return null;
    const cells: Array<{ id: ProductFeatureId; availability: PlanFeatureAvailability }> = [];
    for (const cell of rawFeatures) {
      if (!cell || typeof cell !== 'object') return null;
      const { id, availability } = cell as { id?: unknown; availability?: unknown };
      if (!isProductFeatureId(id)) continue;
      if (!isAvailability(availability)) return null;
      cells.push({ id, availability });
    }
    out.push({
      key,
      name_en,
      name_ar,
      is_current,
      limits: { max_clients, max_seats },
      features: cells,
    });
  }
  return out;
}

function formatDateAr(value: string | null): string {
  if (!value) return 'غير محدد';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'غير محدد';
  return parsed.toLocaleDateString('ar-SA');
}

export function AgencyBillingPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<AgencyCommercialSummaryData | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getAgencyCommercialSummary({ signal: controller.signal })
      .then((data) => setSummary(data))
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(describeError(err));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  // `inline` is the only variant that renders `title`; `page` drops it.
  if (loading) return <LoadingState title="جاري تحميل بيانات الاشتراك…" variant="inline" />;
  if (error || !summary) {
    // No `error` prop on purpose: the message below is the whole answer,
    // so no wire code is printed beneath it.
    return (
      <ErrorState
        title="تعذّر تحميل بيانات الاشتراك"
        description={error ?? BILLING_ERROR_FALLBACK_AR}
      />
    );
  }

  const { plan, subscription, commercial, limits } = summary;
  const clientCreationOpen = subscriptionPermitsClientCreation(subscription.state);

  // ---------------------------------------------------------------------
  // THE THREE TASK 301B BLOCKS ARE REQUIRED BY THE CONTRACT AND ABSENT FOR
  // A WINDOW ON EVERY ROLLOUT. Merging this PR auto-deploys the frontend
  // through Vercel, while `get-agency-commercial-summary` is deployed BY
  // HAND afterwards. Between those two moments the deployed function is
  // Task 301's, which sends `plan` / `subscription` / `commercial` /
  // `limits` and nothing else. Reading `.map` off an absent `features`
  // there would white-screen `/app/billing` for every agency until an
  // operator finishes the deploy.
  //
  // So each block is validated once and rendered only when it arrived.
  // The four Task 301 cards keep working throughout; the new ones appear
  // when the server can answer them. This is NOT a nullable contract —
  // types.ts still requires all three — it is a deploy-ordering guard, and
  // the rollout order in the task file is still migration -> function ->
  // frontend.
  // ---------------------------------------------------------------------
  const usage = asUsage(summary.usage);
  const features = asFeatureList(summary.features);
  const catalogue = asCatalogue(summary.catalogue);
  const currentPlanIsPublic = (catalogue ?? []).some((entry) => entry.is_current);

  return (
    <section className="stack-md" data-testid="agency-billing-page">
      <div className="page-head">
        <div>
          <span className="pill">اشتراك الوكالة</span>
          <h1 className="h-page" style={{ marginTop: 10 }}>
            اشتراك الوكالة في <em className="serif italic">Super Agency</em>
          </h1>
          <p className="sub">
            هذه الصفحة تعرض اشتراك وكالتك في منصة Super Agency فقط. باقات الخدمات التي تبيعها
            لعملائك تُدار في صفحة <a href="/app/services">الخدمات والباقات</a>، وهي شيء مختلف تماماً
            عن هذا الاشتراك.
          </p>
        </div>
        <div style={{ minWidth: 180, textAlign: 'end' }}>
          <span
            className={commercial.commercial_state === 'activated' ? 'pill pill-success' : 'pill'}
          >
            {COMMERCIAL_STATE_LABELS_AR[commercial.commercial_state]}
          </span>
        </div>
      </div>

      <section className="card" style={{ padding: 20 }} data-testid="billing-plan-card">
        <div className="sec-title">
          <h2>الخطة</h2>
          <span className="meta">للقراءة فقط</span>
        </div>
        {/* Names only. `plan.key` stays on the wire; it is not shown. */}
        <div style={GRID}>
          <Fact label="اسم الخطة" value={plan.name_ar} />
          <Fact label="الاسم بالإنجليزية" value={plan.name_en} ltr />
        </div>
        <p className="tiny" style={{ marginTop: 14 }}>
          تغيير الخطة يتم عبر فريق Super Agency ولا يمكن تنفيذه ذاتياً من داخل المنتج.
        </p>
      </section>

      <section className="card" style={{ padding: 20 }} data-testid="billing-subscription-card">
        <div className="sec-title">
          <h2>الاشتراك</h2>
          <span className={clientCreationOpen ? 'pill pill-success' : 'pill'}>
            {SUBSCRIPTION_STATE_LABELS_AR[subscription.state]}
          </span>
        </div>
        <div style={GRID}>
          <Fact label="تاريخ بداية الاشتراك" value={formatDateAr(subscription.started_at)} />
          <Fact
            label="نهاية الفترة الحالية"
            value={formatDateAr(subscription.current_period_ends_at)}
          />
        </div>
        {!clientCreationOpen ? (
          <div className="banner banner-error" role="status" style={{ marginTop: 16 }}>
            إنشاء عملاء جدد متوقف ما دام الاشتراك ليس «نشط». الحدود المعروضة بالأسفل تصف الخطة، ولا
            تعني أن الإنشاء متاح الآن.
          </div>
        ) : null}
      </section>

      <section className="card" style={{ padding: 20 }} data-testid="billing-commercial-card">
        <div className="sec-title">
          <h2>الحالة التجارية</h2>
          <span className="meta">يديرها فريق المنصة</span>
        </div>
        <div style={GRID}>
          <Fact label="الحالة" value={COMMERCIAL_STATE_LABELS_AR[commercial.commercial_state]} />
          <Fact label="تاريخ التفعيل" value={formatDateAr(commercial.activated_at)} />
        </div>
        <p className="tiny" style={{ marginTop: 14 }}>
          ثلاث حالات مستقلة: الحالة التجارية تحدد إن كان الحساب مسموحاً له بالعمل أصلاً، وحالة
          الاشتراك تحدد إن كانت مزايا الخطة سارية الآن، وحالة الدفع سجل إداري لا يمنح صلاحية ولا
          يمنعها.
        </p>
      </section>

      <section className="card" style={{ padding: 20 }} data-testid="billing-payment-card">
        <div className="sec-title">
          <h2>حالة الدفع</h2>
          <span className="pill">{PAYMENT_STATUS_LABELS_AR[commercial.payment_status]}</span>
        </div>
        <p className="body">
          التفعيل يدوي والسداد يتم عبر <strong>تحويل بنكي</strong> بالتنسيق مع فريق Super Agency.
        </p>
        <div className="banner" role="note" style={{ marginTop: 16 }}>
          لا يوجد دفع إلكتروني داخل المنتج: لا صفحة سداد، ولا بطاقة، ولا فواتير آلية، ولا ترقية
          ذاتية من هذه الصفحة. لتغيير الخطة أو تسجيل دفعة، تواصل مع فريق Super Agency.
        </div>
        <p className="tiny" style={{ marginTop: 14 }}>
          حالة الدفع هنا سجل إداري فقط ولا تُستخدم كبوابة: إيقاف الحساب أو تفعيله قرار منفصل يتخذه
          فريق المنصة.
        </p>
      </section>

      <section className="card" style={{ padding: 20 }} data-testid="billing-limits-card">
        <div className="sec-title">
          <h2>حدود الخطة</h2>
          <span className="meta">تصف الخطة</span>
        </div>
        <div style={GRID}>
          <Fact
            label="الحد الأقصى للعملاء"
            value={formatLimitAr(limits.max_clients)}
            testId="billing-limit-max-clients"
          />
          <Fact
            label="الحد الأقصى للمقاعد"
            value={formatLimitAr(limits.max_seats)}
            testId="billing-limit-max-seats"
          />
        </div>
        {limits.max_clients === 0 || limits.max_seats === 0 ? (
          <p className="tiny" style={{ marginTop: 14 }}>
            القيمة «0» تعني أن السعة المتاحة الآن صفر. لتعديل الحد تواصل مع فريق Super Agency.
          </p>
        ) : null}
      </section>

      {/*
        Task 301B — CURRENT USAGE. The two numbers are counted server-side
        exactly the way enforcement counts them, through a definer RPC:
        the caller's own RLS cannot see every client (finance_manager
        holds `billing.read` but is not admitted by `clients_select`), so
        a caller-scoped count would contradict the cap printed above it.
      */}
      {usage ? (
        <section className="card" style={{ padding: 20 }} data-testid="billing-usage-card">
          <div className="sec-title">
            <h2>الاستهلاك الحالي</h2>
            <span className="meta">مقابل حدود الخطة</span>
          </div>
          <div style={GRID}>
            <Fact
              label="العملاء"
              value={formatUsageAr(usage.clients, limits.max_clients)}
              testId="billing-usage-clients"
            />
            <Fact
              label="المقاعد"
              value={formatUsageAr(usage.seats, limits.max_seats)}
              testId="billing-usage-seats"
            />
          </div>
          <p className="tiny" style={{ marginTop: 14 }}>
            تُحتسب الأرقام كما يحتسبها النظام عند الإنشاء: العميل المؤرشف لا يُحتسب، والدور المنتهي
            لا يشغل مقعداً.
          </p>
        </section>
      ) : null}

      {/*
        Task 301B — EFFECTIVE capabilities: the per-organization overrides
        layered over the current plan's defaults, all off when the
        subscription state is not entitled. Product labels only — the
        entitlement-store feature keys never reach this file.
      */}
      {features ? (
        <section className="card" style={{ padding: 20 }} data-testid="billing-features-card">
          <div className="sec-title">
            <h2>ما تشمله خطتك حالياً</h2>
            <span className="meta">الوضع الفعلي</span>
          </div>
          <div style={GRID}>
            {features.map((feature) => (
              <Fact
                key={feature.id}
                label={PRODUCT_FEATURE_LABELS_AR[feature.id]}
                value={formatIncludedAr(feature.included)}
                testId={`billing-feature-${feature.id}`}
              />
            ))}
          </div>
          <p className="tiny" style={{ marginTop: 14 }}>
            هذه القائمة تعكس ما هو مفعّل لوكالتك الآن، بما في ذلك أي استثناءات خاصة أضافها فريق
            المنصة، وليست وصفاً عاماً للخطة.
          </p>
        </section>
      ) : null}

      {/*
        Task 301B — the PUBLIC catalogue, read dynamically from
        plans/plan_features. No plan key, name, cap or feature value is
        hardcoded here; the server sends whatever is currently published.
      */}
      {catalogue ? (
        <section className="card" style={{ padding: 20 }} data-testid="billing-catalogue-card">
          <div className="sec-title">
            <h2>مقارنة الخطط</h2>
            <span className="meta">للاطلاع فقط</span>
          </div>
          {catalogue.length === 0 ? (
            <p className="body">لا توجد خطط معروضة للعموم حالياً.</p>
          ) : (
            <PlanCatalogueTable catalogue={catalogue} />
          )}
          <p className="tiny" style={{ marginTop: 14 }}>
            «حسب الاتفاق» تعني أن الخطة لا تحمل قيماً محددة مسبقاً وتُضبط بالتنسيق مع فريق Super
            Agency. ما هو مفعّل لوكالتك الآن معروض في البطاقتين أعلاه.
          </p>
          {!currentPlanIsPublic ? (
            <p className="tiny" style={{ marginTop: 8 }} data-testid="billing-catalogue-not-listed">
              خطتك الحالية غير معروضة ضمن الخطط العامة أعلاه؛ حدودها وما تشمله معروضان في البطاقات
              السابقة.
            </p>
          ) : null}
        </section>
      ) : null}

      {/*
        Task 301B — the ONE new affordance, and it is navigation.

        This is a `Link` to the existing `/app/support` page. It writes
        nothing, calls no endpoint, and creates no record — there is no
        request table in this programme and no mutation in this file. The
        copy beside it says exactly that, so nobody reads a page visit as
        a submitted request.
      */}
      <section className="card" style={{ padding: 20 }} data-testid="billing-plan-change-card">
        <div className="sec-title">
          <h2>تغيير الخطة</h2>
          <span className="meta">عبر فريق المنصة</span>
        </div>
        <p className="body">
          تغيير الخطة يتم بالتنسيق مع فريق Super Agency ولا يُنفَّذ ذاتياً من داخل المنتج.
        </p>
        <div style={{ marginTop: 16 }}>
          <Link className="btn btn-ghost" to="/app/support" data-testid="billing-support-link">
            طلب تغيير الخطة
          </Link>
        </div>
        <p className="tiny" style={{ marginTop: 14 }}>
          هذا الرابط ينقلك إلى صفحة المساعدة فقط. الضغط عليه لا يُرسل طلباً ولا يُسجّل أي تغيير على
          اشتراكك.
        </p>
      </section>
    </section>
  );
}

/**
 * Task 301B — the public comparison table.
 *
 * Rendered from props and exported, so its emitted markup can be asserted
 * directly. `renderToStaticMarkup` never reaches the page's loaded branch
 * — effects do not run on the server — so before this extraction the "no
 * plan key anywhere on the page" rule was pinned only by source grep, and
 * a source grep is exactly the pin that stayed green while `starter_v1`
 * was on screen. Now the rule is executed against real markup.
 *
 * THE COLUMN TEST IDS ARE POSITIONAL, NOT KEYED. `data-testid` is a DOM
 * attribute: `billing-catalogue-clients-${entry.key}` put the entitlement
 * store's internal plan id into the served HTML. Invisible to a reader,
 * and therefore invisible to a visible-text QA scan, but present. The
 * column index answers the same question for a test, and the header cell
 * beside it carries the plan's public Arabic name — which is what a human
 * reading the page has to go on anyway.
 */
export function PlanCatalogueTable({
  catalogue,
}: {
  readonly catalogue: ReadonlyArray<PlanCatalogueEntry>;
}) {
  return (
    <table className="tbl" data-testid="billing-catalogue-table">
      <thead>
        <tr>
          <th>البند</th>
          {catalogue.map((entry, column) => (
            <th key={entry.key} data-testid={`billing-catalogue-plan-col-${column}`}>
              {entry.name_ar}
              {entry.is_current ? (
                <span className="pill pill-success" style={{ marginInlineStart: 6 }}>
                  خطتك
                </span>
              ) : null}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>الحد الأقصى للعملاء</td>
          {catalogue.map((entry, column) => (
            <td key={entry.key} data-testid={`billing-catalogue-clients-col-${column}`}>
              {formatPlanLimitAr(entry.limits.max_clients)}
            </td>
          ))}
        </tr>
        <tr>
          <td>الحد الأقصى للمقاعد</td>
          {catalogue.map((entry, column) => (
            <td key={entry.key} data-testid={`billing-catalogue-seats-col-${column}`}>
              {formatPlanLimitAr(entry.limits.max_seats)}
            </td>
          ))}
        </tr>
        {featureRows(catalogue).map((row) => (
          <tr key={row.id}>
            <td>{PRODUCT_FEATURE_LABELS_AR[row.id]}</td>
            {row.cells.map((cell) => (
              <td key={cell.key}>{formatAvailabilityAr(cell.availability)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Transpose the catalogue into one row per capability.
 *
 * The comparison reads plan-by-column, capability-by-row, which is how a
 * reader compares two plans. The wire is plan-major, so the transpose
 * happens here — in a pure function, so the "same feature set, same
 * order, one cell per plan" property is unit-tested rather than eyeballed
 * in JSX.
 */
export function featureRows(catalogue: ReadonlyArray<PlanCatalogueEntry>): ReadonlyArray<{
  readonly id: ProductFeatureId;
  readonly cells: ReadonlyArray<{
    readonly key: string;
    readonly availability: PlanFeatureAvailability;
  }>;
}> {
  const ids: ProductFeatureId[] = [];
  for (const entry of catalogue) {
    for (const feature of entry.features) {
      if (!ids.includes(feature.id)) ids.push(feature.id);
    }
  }
  return ids.map((id) => ({
    id,
    cells: catalogue.map((entry) => ({
      key: entry.key,
      // A plan that does not state this capability at all is reported as
      // «حسب الاتفاق» rather than silently as excluded: an absent answer
      // is not a "no".
      availability:
        entry.features.find((f) => f.id === id)?.availability ??
        ('by_agreement' as PlanFeatureAvailability),
    })),
  }));
}

const GRID = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
  gap: 14,
} as const;

function Fact({
  label,
  value,
  ltr = false,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly ltr?: boolean;
  readonly testId?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span className="lbl">{label}</span>
      <span className="body" dir={ltr ? 'ltr' : undefined} data-testid={testId}>
        {value || '—'}
      </span>
    </div>
  );
}

/** The page carries no retry button, so the copy has to name the action. */
export const BILLING_ERROR_FALLBACK_AR =
  'تعذّر تحميل بيانات الاشتراك حالياً. حدّث الصفحة، وإذا تكرّر الأمر تواصل مع فريق Super Agency.';

/** Every branch returns Arabic prose, so no wire code can reach the screen. */
export function describeError(err: unknown): string {
  if (isApiError(err)) {
    if (err.code === 'permission_denied') return 'لا تملك صلاحية عرض اشتراك الوكالة.';
    if (err.code === 'commercial_activation_required') {
      return 'اشتراك الوكالة غير متاح في حالة الحساب الحالية.';
    }
    if (err.code === 'not_found') return 'لا يوجد اشتراك مسجّل لهذه الوكالة بعد.';
  }
  if (isNetworkError(err)) {
    return 'تعذّر الاتصال بالخادم. تحقّق من اتصالك بالإنترنت ثم حدّث الصفحة.';
  }
  return BILLING_ERROR_FALLBACK_AR;
}
